import uuid4 from "uuid4";
import {
  Series,
  SeriesCharacterInfo,
  SeriesNameTranslationsInfo,
  SeriesTermInfo
} from "../models/interfaces/series.interface.ts";
import {CharacterGender} from "../models/types/charecterGender.ts";
import {SeriesGenresEnum} from "../models/enums/seriesGenres.enum.ts";

export interface SeriesModalState {
  series: Series | null
}

export type SeriesModalAction =
  | { type: "INIT_SERIES"; payload: Series }
  | { type: "ADD_NEW_NAME_TRANSLATION"; }
  | { type: "SET_NAME_TRANSLATION_KEY"; payload: { oldValue: string; newValue: string } }
  | { type: "SET_NAME_TRANSLATION_VALUE"; payload: { languageCode: string; translation: string } }
  | { type: "REMOVE_NAME_TRANSLATION"; payload: { languageCode: string } }
  | { type: "ADD_NEW_CHARACTER"; }
  | { type: "EDIT_CHARACTER_NAME"; payload: { characterId: string; newName: string } }
  | { type: "EDIT_CHARACTER_GENDER"; payload: { characterId: string; gender: CharacterGender } }
  | { type: "EDIT_CHARACTER_AGE_RANGE"; payload: { characterId: string; ageRange: string } }
  | { type: "SET_CHARACTER_OTHER_NAME"; payload: { characterId: string, otherNames: string[] } }
  | { type: "DELETE_CHARACTER"; payload: { characterId: string } }
  | { type: "SET_GENRES"; payload: SeriesGenresEnum[] }
  | { type: "ADD_NEW_TERM"; }
  | { type: "EDIT_TERM_NAME"; payload: { termId: string; newName: string } }
  | { type: "EDIT_TERM_SOURCE_LANGUAGE"; payload: { termId: string; languageName: string } }
  | { type: "EDIT_TERM_TAG_TYPE"; payload: { termId: string; tagType: string; } }
  | { type: "EDIT_TERM_COMPLEXITY_SCORE"; payload: { termId: string; score: number } }
  | { type: "EDIT_TERM_MEANING"; payload: { termId: string; meaning: string } }
  | { type: "SET_TERM_SYNONYMS"; payload: { termId: string; synonyms: string[] } }
  | {
  type: "SET_TERM_TRANSLATION_KEY";
  payload: { termId: string; oldTargetLanguage: string; newTargetLanguage: string; }
}
  | { type: "SET_TERM_TRANSLATION"; payload: { termId: string; targetLanguage: string; translation: string; } }
  | { type: "REMOVE_TERM_TRANSLATION"; payload: { termId: string; targetLanguage: string; } }
  | { type: "DELETE_TERM"; payload: { termId: string } }


export const initialSeriesModalState: SeriesModalState = {
  series: null
}

export function seriesModalReducer(
  state: SeriesModalState,
  action: SeriesModalAction
): SeriesModalState {
  switch (action.type) {
    case "INIT_SERIES":
      return {
        ...state,
        series: action.payload
      };
    case "ADD_NEW_NAME_TRANSLATION": {
      if (!state.series) return state;

      // Create a unique key for new entry
      let i: number = 1;
      const newKey: string = "new";
      const updatedTranslations: SeriesNameTranslationsInfo = {...state.series.info.nameTranslations};
      while (Object.prototype.hasOwnProperty.call(updatedTranslations, `${newKey}${i}`)) {
        i++;
      }
      updatedTranslations[`${newKey}${i}`] = "";
      return {
        ...state,
        series: {
          ...state.series,
          info: {
            ...state.series.info,
            nameTranslations: updatedTranslations
          }
        }
      };
    }
    case "SET_NAME_TRANSLATION_KEY": {
      if (!state.series) return state;

      const {oldValue, newValue} = action.payload;

      const updatedTranslations: SeriesNameTranslationsInfo = {};
      for (const key in state.series.info.nameTranslations) {
        if (key === oldValue) {
          updatedTranslations[newValue] = state.series.info.nameTranslations[oldValue];
        } else {
          updatedTranslations[key] = state.series.info.nameTranslations[key];
        }
      }

      return {
        ...state,
        series: {
          ...state.series,
          info: {
            ...state.series.info,
            nameTranslations: updatedTranslations
          }
        }
      };
    }

    case "SET_NAME_TRANSLATION_VALUE": {
      if (!state.series) return state;

      const {languageCode, translation} = action.payload;
      const updatedTranslations: SeriesNameTranslationsInfo = {...state.series.info.nameTranslations};

      updatedTranslations[languageCode] = translation;
      return {
        ...state,
        series: {
          ...state.series,
          info: {
            ...state.series.info,
            nameTranslations: updatedTranslations
          }
        }
      };
    }

    case "REMOVE_NAME_TRANSLATION": {
      if (!state.series) return state;

      const {languageCode} = action.payload;
      const updatedTranslations: SeriesNameTranslationsInfo = {...state.series.info.nameTranslations};

      if (Object.prototype.hasOwnProperty.call(updatedTranslations, languageCode)) {
        delete updatedTranslations[languageCode];
      }

      return {
        ...state,
        series: {
          ...state.series,
          info: {
            ...state.series.info,
            nameTranslations: updatedTranslations
          }
        }
      };
    }
    case "ADD_NEW_CHARACTER":
      if (state.series) {
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              characters: [
                ...state.series.info.characters,
                {
                  characterId: uuid4(),
                  originalLanguageName: "",
                  name: "",
                  otherNames: [],
                  ageRange: "",
                }
              ]
            }
          }
        }
      }
      return state;
    case "EDIT_CHARACTER_NAME": {
      if (state.series) {
        const {characterId, newName} = action.payload;
        const updatedCharacters: SeriesCharacterInfo[] = state.series.info.characters.map(character => {
          if (character.characterId === characterId) {
            return {...character, name: newName};
          }
          return character;
        })
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              characters: updatedCharacters
            }
          }
        };
      }
      return state;
    }
    case "EDIT_CHARACTER_GENDER": {
      if (state.series) {
        const {characterId, gender} = action.payload;
        console.log("Editing character", characterId, gender)
        const updatedCharacters: SeriesCharacterInfo[] = state.series.info.characters.map(character => {
          if (character.characterId === characterId) {
            return {...character, gender};
          }
          return character;
        })
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              characters: updatedCharacters
            }
          }
        };
      }
      return state
    }
    case "EDIT_CHARACTER_AGE_RANGE": {
      if (state.series) {
        const {characterId, ageRange} = action.payload;
        const updatedCharacters = state.series.info.characters.map(character => {
          if (character.characterId === characterId) {
            return {...character, ageRange};
          }
          return character;
        })
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              characters: updatedCharacters
            }
          }
        };
      }
      return state;
    }
    case "SET_CHARACTER_OTHER_NAME": {
      if (state.series) {
        const {characterId, otherNames} = action.payload;
        const updatedCharacters: SeriesCharacterInfo[] = state.series.info.characters.map(character => {
          if (character.characterId === characterId) {
            return {...character, otherNames};
          }
          return character;
        })
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              characters: updatedCharacters
            }
          }
        };
      }
      return state;
    }
    case "DELETE_CHARACTER": {
      if (state.series) {
        const {characterId} = action.payload;
        const updatedCharacters: SeriesCharacterInfo[] = state.series.info.characters.filter(character => character.characterId !== characterId);
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              characters: updatedCharacters
            }
          }
        };
      }
      return state;
    }
    case "SET_GENRES":
      if (state.series) {
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              genres: action.payload
            }
          }
        };
      }
      return state;
    case "ADD_NEW_TERM": {
      if (state.series) {
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: [
                ...state.series.info.terms,
                {
                  termId: uuid4(),
                  sourceLanguage: state.series.info.sourceLanguage,
                  term: "",
                  tagType: "",
                  otherTag: "",
                  synonyms: [],
                  localizationComplexityScore: 0,
                  meaning: "",
                  translations: {}
                }
              ]
            }
          }
        };
      }
      return state;
    }
    case "EDIT_TERM_NAME": {
      if (state.series) {
        const {termId, newName} = action.payload;
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.map(term => {
          if (term.termId === termId) {
            return {...term, term: newName};
          }
          return term;
        });
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };

      }
      return state;
    }
    case "EDIT_TERM_SOURCE_LANGUAGE": {
      if (state.series) {
        const {termId, languageName} = action.payload;
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.map(term => {
          if (term.termId === termId) {
            return {...term, sourceLanguage: languageName};
          }
          return term;
        });
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };

      }
      return state;
    }
    case "EDIT_TERM_TAG_TYPE": {
      if (state.series) {
        const {termId, tagType} = action.payload;
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.map(term => {
          if (term.termId === termId) {
            return {...term, tagType};
          }
          return term;
        });
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };

      }
      return state;
    }
    case "EDIT_TERM_COMPLEXITY_SCORE": {
      if (state.series) {
        const {termId, score} = action.payload;
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.map(term => {
          if (term.termId === termId) {
            return {...term, localizationComplexityScore: score};
          }
          return term;
        });
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };

      }
      return state;
    }
    case "EDIT_TERM_MEANING": {
      if (state.series) {
        const {termId, meaning} = action.payload;
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.map(term => {
          if (term.termId === termId) {
            return {...term, meaning};
          }
          return term;
        });
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };

      }
      return state;
    }
    case "SET_TERM_SYNONYMS": {
      if (state.series) {
        const {termId, synonyms} = action.payload;
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.map(term => {
          if (term.termId === termId) {
            return {...term, synonyms};
          }
          return term;
        });
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };

      }
      return state;
    }
    case "SET_TERM_TRANSLATION_KEY": {
      if (state.series) {
        const {termId, oldTargetLanguage, newTargetLanguage} = action.payload;
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.map((term: SeriesTermInfo) => {
          if (term.termId === termId) {
            const updatedTranslations = {...term.translations};
            if (Object.prototype.hasOwnProperty.call(updatedTranslations, oldTargetLanguage)) {
              updatedTranslations[newTargetLanguage] = updatedTranslations[oldTargetLanguage];
              delete updatedTranslations[oldTargetLanguage];
            }
            return {...term, translations: updatedTranslations};
          }
          return term;
        });
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };
      }
      return state;
    }
    case "SET_TERM_TRANSLATION": {
      if (state.series) {
        const {termId, targetLanguage, translation} = action.payload;
        console.log(termId, targetLanguage, translation)
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.map((term: SeriesTermInfo) => {
          if (term.termId === termId) {
            const updatedTranslations = {...term.translations};
            updatedTranslations[targetLanguage] = translation
            return {...term, translations: updatedTranslations};
          }
          return term;
        });
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };
      }
      return state;
    }
    case "REMOVE_TERM_TRANSLATION": {
      if (state.series) {
        const {termId, targetLanguage} = action.payload;
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.map((term: SeriesTermInfo) => {
          if (term.termId === termId) {
            const updatedTranslations = {...term.translations};
            if (Object.prototype.hasOwnProperty.call(updatedTranslations, targetLanguage)) {
              delete updatedTranslations[targetLanguage];
            }
            return {...term, translations: updatedTranslations};
          }
          return term;
        });
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };
      }
      return state;
    }
    case "DELETE_TERM": {
      if (state.series) {
        const {termId} = action.payload;
        const updatedTerms: SeriesTermInfo[] = state.series.info.terms.filter(term => term.termId !== termId);
        return {
          ...state,
          series: {
            ...state.series,
            info: {
              ...state.series.info,
              terms: updatedTerms
            }
          }
        };
      }
      return state;
    }
    default:
      return state;
  }
}
